import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { FormFieldset } from '../../../../components/inputs/FormFieldset';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { pressReleasesApi } from '../pressReleaseApi';
import type { PressReleasePostingItem } from '../pressReleaseTypes';

/**
 * Tick the channels a PUBLISHED press release was posted on. The save REPLACES the set; the server
 * stamps who ticked each and when.
 *
 * **A deleted channel is shown only where already ticked**, and can be unticked but not ticked
 * again — the server refuses a fresh tick on a retired channel, and the box says so first.
 *
 * Autogenerate ticks a random set of the LIVE channels — the only values this form has.
 */
export function PostingsModal({
  releaseId,
  releaseTitle,
  lines,
  onClose,
  onSaved,
}: {
  releaseId: string;
  releaseTitle: string;
  lines: PressReleasePostingItem[];
  onClose: () => void;
  onSaved: (next: PressReleasePostingItem[]) => void;
}) {
  const toast = useToast();

  const [ticked, setTicked] = useState<Set<string>>(() => new Set(lines.filter((l) => l.isPosted).map((l) => l.channelId)));
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  function toggle(channelId: string) {
    setTicked((current) => {
      const next = new Set(current);
      if (next.has(channelId)) next.delete(channelId);
      else next.add(channelId);
      return next;
    });
  }

  async function save() {
    if (!confirmed || saving) return;

    setSaving(true);
    setError(null);

    try {
      const next = await pressReleasesApi.savePostings(releaseId, [...ticked]);
      const count = next.filter((l) => l.isPosted).length;

      toast.success(`"${releaseTitle}" is recorded as posted on ${count} ${count === 1 ? 'channel' : 'channels'}.`);
      onSaved(next);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save where it was posted.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'press-release-postings' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Record where ${releaseTitle} was posted`}
      size="lg"
      id="press-release-postings-modal"
      autogenerate={{
        onGenerate: () =>
          setTicked(new Set(lines.filter((l) => l.channelIsActive && Math.random() < 0.6).map((l) => l.channelId))),
        disabled: saving,
      }}
      confirm={{ id: 'press-release-postings-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="press-release-postings-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="press-release-postings-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!confirmed || saving}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <FormFieldset legend="Posted on">
          {/* The id the server's `channelIds` field error resolves to. */}
          <ul id="press-release-postings-channel-ids" className="flex flex-col gap-2">
            {lines.map((line) => {
              const id = `press-release-postings-${line.channelId}`;
              const checked = ticked.has(line.channelId);
              const retired = !line.channelIsActive;

              return (
                <li key={line.channelId}>
                  <label htmlFor={id} className="flex items-center gap-2 text-sm text-slate-600">
                    <Checkbox
                      id={id}
                      name={id}
                      checked={checked}
                      onChange={() => toggle(line.channelId)}
                      // A deleted channel can be UNticked, never ticked afresh.
                      disabled={saving || (retired && !checked)}
                    />
                    <span>
                      {line.channelName}
                      {retired ? <span className="text-xs text-slate-500"> — deleted channel; can be unticked, not ticked again</span> : null}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </FormFieldset>
      </div>
    </Modal>
  );
}
